import { Injectable } from '@angular/core'
import { ApplicationType, SyncPairDto, SyncPhaseDto } from 'src/app/services/dtos'
import { ApplicationRecoveryPointDto } from 'src/app/services/dtos'
import { Application, ApplicationProviderService } from 'src/app/account/services/application.service'
import { AccountService } from 'src/app/account/services/account.service'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ApiService } from 'src/app/services/api.service'
import { buildMockApplication } from './mocks/showcase-mock-core'
import {
  LAUNCH_TEST_SANDBOX_SCENARIOS,
  SANDBOX_CONFIG_SCENARIOS,
  SandboxScenario,
  buildMockAzureQuarantinedResources,
  buildMockQuarantinedResources,
} from './mocks/showcase-modals-mocks'
import { buildMockRetainedRecoveryPoints, buildMockRetentionApplications } from './mocks/showcase-retention-mocks'
import { UsageBillingScenario, buildUsageBillingScenarios } from './mocks/showcase-usage-billing-mocks'
import { StacksetsJobScenarioKey } from './mocks/showcase-stacksets-mocks'

// The showcase spans both clouds, so it can't reuse the service's AccessMethodType
// (AWS access methods only).
export type LegacyTemplateFlow = 'cloudformation' | 'stacksets' | 'azure'

/**
 * The only account the showcase ever addresses. Every account-scoped call is
 * patched, so this id never reaches the network — but it keeps a real customer
 * account from being the one the page is pointed at.
 */
export const MOCK_TEST_ACCOUNT_ID = 'MOCK-TEST-account-id'

const MOCK_TEST_SYNC_PAIR = {
  sourceAwsAccountId: '111111111111',
  sourceRegion: 'us-east-1',
  targetAwsAccountId: '222222222222',
  targetRegion: 'us-west-2',
  sourceEndpointUri: 'MOCK-TEST-source-endpoint',
  targetEndpointUri: 'MOCK-TEST-target-endpoint',
} as SyncPairDto

/**
 * Mock fixtures and scenario flags shared across showcase sections.
 *
 * Sections mount and unmount in whatever order the reviewer clicks, so anything a
 * patched service method reads has to outlive them and carry its own default —
 * a modals dialog reads `legacyTemplateFlow` whether or not that section ever
 * opened. Sections write here; `showcase-mock-patches.ts` reads.
 */
@Injectable()
export class TestPageMockState {
  syncPair: SyncPair | null = null

  /** The app the modals showcase currently drives its dialogs with. */
  application: Application | null = null

  // Mock applications keyed by sync phase so each modal opens with the right
  // state (STANDBY for launch flows, FAILOVER for conclude/unquarantine, etc).
  mockStandbyApp: Application | null = null
  mockFailoverApp: Application | null = null
  mockFailoverTestApp: Application | null = null
  // A FAILOVER_TEST app whose restore has completed, in its own region pair so
  // it doesn't co-mingle with the still-restoring mockFailoverTestApp used by
  // "Cancel test (early)". Drives the full conclude-test flow (test outcome +
  // generate report); without a completed restore the dialog goes all-cancel.
  mockFailoverTestCompletedApp: Application | null = null
  // Azure counterparts for the quarantine paths: launch-recovery renders its
  // quarantine step for Azure, and unquarantine releases Azure VMs. The AWS
  // mocks can't preview either.
  mockAzureStandbyApp: Application | null = null
  mockAzureFailoverApp: Application | null = null
  // A separate FAILOVER_TEST app with the advanced sandbox flag on, so both
  // basic and advanced sandbox-settings variants can be previewed.
  mockSandboxAdvancedApp: Application | null = null

  // Sibling mocks per phase — needed so dialog candidate lists are populated.
  siblingStandbyApps: Application[] = []
  siblingFailoverApps: Application[] = []
  siblingFailoverTestApps: Application[] = []

  // Network-sandbox scenario anchors, keyed by scenario key.
  sandboxScenarioApps: Application[] = []
  launchTestSandboxAppMap = new Map<string, Application>()
  sandboxConfigAppMap = new Map<string, Application>()

  // ── Flags the patched service methods read ───────────────────────
  // Capabilities reported OFF by the patched entitledForCapability(). Empty by
  // default (everything on); network-sandbox scenarios populate it.
  mockDisabledCapabilities = new Set<string>()
  // Switches the patched fetchFirewallLogs between an empty and a populated list.
  mockFirewallEventsPopulated = false
  // Drives the access-template / access-test patches, which the legacy-templates
  // flow picker owns but the modals update-access dialog also consumes.
  legacyTemplateFlow: LegacyTemplateFlow = 'cloudformation'
  // Drives the patched getJob status sequence; the start stamp anchors timed
  // transitions to the reviewer's picker click rather than page load.
  stacksetsJobScenario: StacksetsJobScenarioKey = 'none'
  stacksetsScenarioStartMs = 0
  // Backing rows for the retention showcase; the patched release call mutates them.
  mockRetainedRps: ApplicationRecoveryPointDto[] = []
  mockRetentionApps = new Map<string, Application>()

  readonly usageBillingScenarios: ReadonlyArray<UsageBillingScenario> = buildUsageBillingScenarios()
  usageBillingScenarioKey: string = this.usageBillingScenarios[0].key

  /** The account's real entitlement, captured before Usage & Billing overlays it. */
  originalEntitlementDto: any

  constructor(
    private readonly apiService: ApiService,
    private readonly applicationProviderService: ApplicationProviderService,
    private readonly accountService: AccountService
  ) {}

  /**
   * Stamps the active Usage & Billing scenario's contract onto the patched entitlement.
   * Merged over the real one so other sections reading it keep working. NO_OVERAGE follows
   * the scenario's billing mode, since that dimension is what makes an account Fixed.
   * Re-run whenever the scenario picker changes.
   */
  applyScenarioEntitlement(): void {
    const contract = this.usageBillingActiveScenario.contract
    ;(this.accountService as any).entitlementDto = {
      ...(this.originalEntitlementDto ?? {}),
      productName: contract.productName,
      expiration: new Date(`${contract.expiration}T00:00:00Z`),
      licensedBillableResourceCount: contract.licensedBillableResourceCount,
      enforceLimits: contract.enforceLimits,
      noOverage: contract.billingMode === 'fixed',
      // Server-derived billing-notification fields (ARP-7304), stamped so the header and any
      // banner / enforcement gate read the scenario rather than the real account.
      billingMode: contract.billingMode,
      usagePercent: contract.usagePercent,
      backupsSuspended: contract.backupsSuspended,
    }
  }

  get legacyTemplateIsAzure(): boolean {
    return this.legacyTemplateFlow === 'azure'
  }

  get usageBillingActiveScenario(): UsageBillingScenario {
    return (
      this.usageBillingScenarios.find((s) => s.key === this.usageBillingScenarioKey) ?? this.usageBillingScenarios[0]
    )
  }

  /** Every mock app the dialog candidate lists need to see. */
  get allMockApps(): Application[] {
    return [
      this.mockStandbyApp,
      ...this.siblingStandbyApps,
      this.mockFailoverApp,
      ...this.siblingFailoverApps,
      this.mockFailoverTestApp,
      this.mockFailoverTestCompletedApp,
      ...this.siblingFailoverTestApps,
      this.mockSandboxAdvancedApp,
      this.mockAzureStandbyApp,
      this.mockAzureFailoverApp,
      ...this.sandboxScenarioApps,
    ].filter(Boolean)
  }

  /** Builds every fixture and seeds the app cache. Called once, after the gate allows access. */
  seedMocks(): void {
    // Built locally rather than fetched: the showcase never addresses a real account.
    this.syncPair = new SyncPair(this.apiService, MOCK_TEST_ACCOUNT_ID, MOCK_TEST_SYNC_PAIR)
    this.mockRetainedRps = buildMockRetainedRecoveryPoints()
    this.mockRetentionApps = buildMockRetentionApplications()

    this.mockStandbyApp = buildMockApplication({
      applicationId: 'MOCK-TEST-standby',
      name: 'MOCK-TEST standby app',
      syncPhase: SyncPhaseDto.STANDBY,
    })
    this.mockFailoverApp = buildMockApplication({
      applicationId: 'MOCK-TEST-failover',
      name: 'MOCK-TEST failover app',
      syncPhase: SyncPhaseDto.FAILOVER,
      // Quarantined resources so the unquarantine dialog renders a populated
      // list (with clean + unclean rows) instead of an empty state.
      restoreResources: buildMockQuarantinedResources(),
    })
    this.mockFailoverTestApp = buildMockApplication({
      applicationId: 'MOCK-TEST-failover-test',
      name: 'MOCK-TEST failover-test app',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
    })
    const restoreCompletedAt = new Date(Date.now() - 30 * 60 * 1000)
    this.mockFailoverTestCompletedApp = buildMockApplication({
      applicationId: 'MOCK-TEST-failover-test-completed',
      name: 'MOCK-TEST failover-test app (restore complete)',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      // Own region pair keeps it out of the "Cancel test (early)" candidate list.
      sourceRegion: 'us-east-2',
      targetRegion: 'us-west-1',
      lastRestoreComplete: restoreCompletedAt,
      lastRestoreSuccess: restoreCompletedAt,
    })
    this.mockSandboxAdvancedApp = buildMockApplication({
      applicationId: 'MOCK-TEST-sandbox-advanced',
      name: 'MOCK-TEST sandbox-advanced app',
      syncPhase: SyncPhaseDto.FAILOVER_TEST,
      advancedNetworkSandbox: true,
    })
    this.mockAzureStandbyApp = buildMockApplication({
      applicationId: 'MOCK-TEST-azure-standby',
      name: 'MOCK-TEST Azure standby app',
      type: ApplicationType.AZURE,
      syncPhase: SyncPhaseDto.STANDBY,
    })
    this.mockAzureFailoverApp = buildMockApplication({
      applicationId: 'MOCK-TEST-azure-failover',
      name: 'MOCK-TEST Azure failover app',
      type: ApplicationType.AZURE,
      syncPhase: SyncPhaseDto.FAILOVER,
      // Azure VM (+ one scale set) quarantined resources so the unquarantine
      // dialog renders the Azure path populated instead of empty.
      restoreResources: buildMockAzureQuarantinedResources(),
    })
    this.siblingStandbyApps = [
      buildMockApplication({
        applicationId: 'MOCK-TEST-standby-2',
        name: 'MOCK-TEST checkout-service',
        syncPhase: SyncPhaseDto.STANDBY,
      }),
      buildMockApplication({
        applicationId: 'MOCK-TEST-standby-3',
        name: 'MOCK-TEST orders-api',
        syncPhase: SyncPhaseDto.STANDBY,
      }),
    ]
    this.siblingFailoverApps = [
      buildMockApplication({
        applicationId: 'MOCK-TEST-failover-2',
        name: 'MOCK-TEST auth-service (failed over)',
        syncPhase: SyncPhaseDto.FAILOVER,
      }),
    ]
    this.siblingFailoverTestApps = [
      buildMockApplication({
        applicationId: 'MOCK-TEST-failover-test-2',
        name: 'MOCK-TEST billing-service (in test)',
        syncPhase: SyncPhaseDto.FAILOVER_TEST,
      }),
    ]
    this.buildSandboxScenarioApps()
    this.seedAppCache(this.allMockApps)
    this.application = this.mockStandbyApp
  }

  /** Restores the released recovery points so the retention flow can be replayed. */
  resetRetainedRecoveryPoints(): void {
    this.mockRetainedRps = buildMockRetainedRecoveryPoints()
  }

  // Reach into the singleton's private cache the same way the other shared
  // singletons are patched — keeps all test-only seeding out of production services.
  seedAppCache(apps: Application[]): void {
    ;(this.applicationProviderService as any).applicationCache = new Map(apps.map((a) => [a.applicationId, a]))
  }

  // Build one anchor app per network-sandbox scenario, each in its own region
  // pair so the launch dialogs' region-scoped candidate lists stay isolated.
  // launch-test anchors are STANDBY; sandbox-settings anchors are FAILOVER_TEST.
  // Scenarios with siblingFailoverTest also seed that many in-test apps in the
  // same region to drive sandboxFrozen / the multi-app "also applies to" notice.
  private buildSandboxScenarioApps(): void {
    this.sandboxScenarioApps = []
    this.launchTestSandboxAppMap.clear()
    this.sandboxConfigAppMap.clear()

    const seedScenario = (
      scenario: SandboxScenario,
      prefix: string,
      index: number,
      anchorPhase: SyncPhaseDto,
      appMap: Map<string, Application>
    ) => {
      const sourceRegion = `${prefix}-src-${index}`
      const targetRegion = `${prefix}-tgt-${index}`
      const anchor = buildMockApplication({
        ...scenario.appOpts,
        applicationId: `${prefix}-${scenario.key}`,
        name: scenario.label,
        syncPhase: anchorPhase,
        sourceRegion,
        targetRegion,
      })
      appMap.set(scenario.key, anchor)
      this.sandboxScenarioApps.push(anchor)
      for (let i = 0; i < (scenario.siblingFailoverTest ?? 0); i++) {
        this.sandboxScenarioApps.push(
          buildMockApplication({
            ...scenario.appOpts,
            applicationId: `${prefix}-${scenario.key}-sibling-${i}`,
            name: `${scenario.label} — sibling ${i + 1}`,
            syncPhase: SyncPhaseDto.FAILOVER_TEST,
            sourceRegion,
            targetRegion,
          })
        )
      }
    }

    LAUNCH_TEST_SANDBOX_SCENARIOS.forEach((scenario, index) =>
      seedScenario(scenario, 'sb-lt', index, SyncPhaseDto.STANDBY, this.launchTestSandboxAppMap)
    )
    SANDBOX_CONFIG_SCENARIOS.forEach((scenario, index) =>
      seedScenario(scenario, 'sb-tc', index, SyncPhaseDto.FAILOVER_TEST, this.sandboxConfigAppMap)
    )
  }
}
